import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  OrganizationUser,
  OrganizationUserRole,
} from '../../interfaces/organization_user.interface';
import { Organization } from '../../interfaces/organization.interface';
import { User, UserStore } from '../../app-state/user.store';
import { Observable } from 'rxjs';

export interface VerifyEmailResponse {
  message: string;
  data: {
    baaEmailSent: boolean;
    complimentaryAccessGranted: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userStore = inject(UserStore);

  constructor(private readonly http: HttpClient) {}

  login(dto: { username: string; password: string }) {
    return this.http.post<{
      message: string;
      data: {
        status: 'MFA_SETUP_REQUIRED' | 'MFA_REQUIRED';
        loginEventId?: string;
        qrCode?: string | null;
        emailTwoFaEnabled: boolean | null;
        authAppTwoFaEnabled: boolean | null;
      };
    }>(environment.api_host + 'auth/login', dto);
  }

  loginStandAlone(
    username: string,
    password: string,
    otp?: string,
    loginEventId?: string,
  ) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('otp', otp ?? '');
    body.set('username', username);
    body.set('password', password);
    if (loginEventId) {
      body.set('loginEventId', loginEventId);
    }

    return this.http.post<{
      message: string;
      data: {
        qrCode: string | null;
        token: string;
        user: User;
        organizationUser: OrganizationUser;
        organization: Organization;
        two_fa_enabled: boolean;
        role: OrganizationUserRole | null;
      };
    }>(environment.api_host + 'auth/login/verify', body.toString(), {
      headers,
      observe: 'response',
    });
  }

  logout() {
    return this.http.post(environment.api_host + 'auth/logout', {});
  }

  signup(dto: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    termsAccepted: boolean;
  }) {
    return this.http.post(environment.api_host + 'auth/sign-up', dto);
  }

  forgotPassword(identifier: string) {
    return this.http.post(environment.api_host + 'auth/forgot-password', {
      email: identifier, // Backend still expects 'email' but it can be either email or username
    });
  }

  resetPassword(dto: { password: string; token: string }) {
    return this.http.post(environment.api_host + 'auth/reset-password', dto);
  }

  verifyEmail(token: string): Observable<VerifyEmailResponse> {
    return this.http.post<VerifyEmailResponse>(
      environment.api_host + 'auth/verify-email',
      { token },
    );
  }

  resendVerificationEmail(email: string) {
    return this.http.post(
      environment.api_host + 'auth/resend-verification-email',
      {
        email,
      },
    );
  }

  getUser() {
    const token = this.userStore.token() ?? localStorage.getItem('token');
    return this.http.post<{
      message: string;
      data: {
        token: string;
        user: User;
        organizationUser: OrganizationUser;
        organization: Organization;
        role: OrganizationUserRole | null;
      };
    }>(environment.api_host + 'auth/user', { token });
  }

  get2FAStatus(): Observable<{
    authAppTwoFaEnabled: boolean;
    emailTwoFaEnabled: boolean;
  }> {
    const token = this.userStore.token() ?? localStorage.getItem('token');
    return this.http.post<{
      authAppTwoFaEnabled: boolean;
      emailTwoFaEnabled: boolean;
    }>(environment.api_host + 'auth/2fa/status', { token });
  }

  sendAuthEmailOtp() {
    const token = this.userStore.token() ?? localStorage.getItem('token');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<{ message: string; resendTimeout: number }>(
      `${environment.api_host}auth/send-auth-email-otp`,
      {},
      { headers },
    );
  }

  setupAuthEmailOtp(otp: string) {
    const body = new HttpParams().set('otp', otp);

    const token = this.userStore.token() ?? localStorage.getItem('token');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<{ message: string }>(
      `${environment.api_host}auth/setup-auth-email-otp`,
      body.toString(),
      { headers },
    );
  }

  sendAuthAppQR() {
    const token = this.userStore.token() ?? localStorage.getItem('token');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<{ success: boolean; qrCode: any }>(
      `${environment.api_host}auth/send-auth-qr-code`,
      {},
      { headers },
    );
  }

  setupAuthAppQR(otp: string) {
    const body = new HttpParams().set('otp', otp);

    const token = this.userStore.token() ?? localStorage.getItem('token');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<{ message: string }>(
      `${environment.api_host}auth/setup-auth-qr-code`,
      body.toString(),
      { headers },
    );
  }

  checkEmail(email: string) {
    return this.http.get<{ taken: boolean }>(
      environment.api_host + 'auth/check-email',
      {
        params: {
          email,
        },
      },
    );
  }
  checkUsername(username: string) {
    return this.http.get<{ taken: boolean }>(
      environment.api_host + 'auth/check-username',
      {
        params: {
          username,
        },
      },
    );
  }

  checkUserExisits(identifier: string) {
    return this.http.get<{ taken: boolean }>(
      environment.api_host + 'auth/check-user-exists',
      {
        params: {
          identifier,
        },
      },
    );
  }

  refreshStandalone() {
    return this.http.post<{
      message: string;
      data: { token: string };
    }>(environment.api_host + 'auth/refresh', {});
  }

  /**
   * Check subscription status from Stripe after login
   */
  checkSubscriptionStatus(): Observable<{
    hasActiveSubscription: boolean;
    subscription?: any;
  }> {
    return this.http.post<{
      hasActiveSubscription: boolean;
      subscription?: any;
    }>(environment.api_host + 'payments/check-subscription-status', {});
  }

  updateValue(dto: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  }) {
    return this.http.post(environment.api_host + 'auth/update-value', dto);
  }

  updatePassword(dto: { id: string; password: string }) {
    return this.http.post(environment.api_host + 'auth/update-password', dto);
  }

  acceptInvite(
    token: string,
    dto: { firstName: string; lastName: string; password: string },
  ) {
    return this.http.post(environment.api_host + 'auth/accept-invite', {
      token,
      ...dto,
    });
  }
}
